import React, { useEffect, useState } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Alert, Divider } from '@mui/material';
import GoogleLogo from '../components/common/GoogleLogo';
import axios from 'axios';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import { API_URL } from '../config/api';
import RecaptchaNotice from '../components/common/RecaptchaNotice';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_SITE_KEY = '6LeDbCEsAAAAANw-bAoYe_8Ldw4phCZQLjkkl_Hw';

const LoginPage: React.FC = () => {
  useSEO({ title: 'ورود', noindex: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/user');
    }
  }, [isAuthenticated, navigate, location]);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaReady(true);
      });
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!recaptchaReady) {
      setError('لطفاً چند لحظه صبر کنید و دوباره تلاش کنید.');
      return;
    }

    try {
      setSubmitting(true);

      // Execute reCAPTCHA v3
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'login' });

      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        recaptchaToken: token,
      });

      const user = {
        name: response.data.fullName || '',
        email: response.data.email,
        role: response.data.role, // Pass the role from API
      };
      login(user as any);
      setSuccess(`خوش آمدید، ${user.name || user.email}`);
      navigate('/user');
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('ورود ناموفق بود. لطفاً دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const GoogleButton = styled(Button)({
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    textTransform: 'none',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#f8f9fa',
      boxShadow: '0 1px 3px 1px rgba(66,64,67,.15)',
    },
    '& .MuiButton-startIcon': {
      marginRight: '10px',
      marginLeft: '2px',
    },
  });

  const LoginButton = styled(Button)({
    backgroundColor: '#1a73e8',
    color: 'white',
    textTransform: 'none',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#1557b0',
      boxShadow: '0 1px 3px 1px rgba(66,64,67,.15)',
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 4 },
          mt: { xs: 4, md: 8 },
          borderRadius: '8px',
          // 14px text, in line with the rest of the site. Typed text stays
          // 16px on phones: iOS zooms the page on smaller inputs.
          '& .MuiInputBase-input': { fontSize: { xs: '16px', md: '14px' } },
          '& .MuiInputLabel-root': { fontSize: '14px' },
          '& .MuiInputLabel-shrink': { fontSize: '16px' },
          '& .MuiButton-root': { fontSize: '14px' },
          '& .MuiButton-sizeSmall': { fontSize: '13px' },
          '& .MuiAlert-message': { fontSize: '14px' },
          '& .MuiDivider-root': { fontSize: '13px' },
        }}
      >
        <Typography component="h1" variant="h6" align="center" gutterBottom sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
          ورود به حساب کاربری
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <GoogleButton
          fullWidth
          variant="outlined"
          startIcon={<GoogleLogo />}
          href={`${API_URL}/auth/google`}
          sx={{ mb: 3 }}
        >
          Continue with Google
        </GoogleButton>

        <Divider sx={{ mb: 3 }}>یا</Divider>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            id="password"
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              component={RouterLink}
              to="/forgot-password"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              رمز عبور را فراموش کرده‌اید؟
            </Button>
          </Box>

          <RecaptchaNotice />

          <LoginButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting || !recaptchaReady}
            sx={{ mt: 3, mb: 2 }}
          >
            {submitting ? 'در حال ورود...' : !recaptchaReady ? 'در حال بارگذاری...' : 'ورود'}
          </LoginButton>
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" dir="rtl">
            حساب کاربری ندارید؟{' '}
            <Button
              component={RouterLink}
              to="/signup"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              ثبت‌نام
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
